import { Box } from "@mui/material";
import { Grid2 as Grid } from "@mui/material";
import { Button, TextField } from "../../../../common";
import { useValidateForm } from "../../../../../hooks";
import { SelectBox } from "../../../../common";
import { DropdownItem, OrganizationProfileInfo } from "../../../../../types";
import { editOrganizationProfileBodySchema } from "../../../../../validations";
import { useEffect, useState } from "react";
import {
  isCreateOrganizationModeAtom,
  selectedOrgIdAtom,
} from "../../../../../atoms";
import { useAtom } from "jotai";

const timeZoneDropdown = [
  // US
  { key: "America/Phoenix", value: 1, country: "US" },
  { key: "America/New_York", value: 2, country: "US" },
  { key: "America/Chicago", value: 3, country: "US" },
  { key: "America/Denver", value: 4, country: "US" },
  { key: "America/Los_Angeles", value: 5, country: "US" },
  { key: "America/Anchorage", value: 6, country: "US" },
  { key: "America/Adak", value: 7, country: "US" },

  // Canada
  { key: "America/Halifax", value: 8, country: "CA" },
  { key: "America/St_Johns", value: 9, country: "CA" },
  { key: "America/Toronto", value: 14, country: "CA" },
  { key: "America/Vancouver", value: 15, country: "CA" },
  { key: "America/Edmonton", value: 16, country: "CA" },
  { key: "America/Winnipeg", value: 17, country: "CA" },

  // Other countries
  { key: "America/Argentina/Buenos_Aires", value: 10, country: "AR" },
  { key: "America/Santiago", value: 11, country: "CL" },
  { key: "America/Bogota", value: 12, country: "CO" },
  { key: "America/Mexico_City", value: 13, country: "MX" },
];

type EditOrganizationProfileBodyProps = {
  defaultValues?: OrganizationProfileInfo;
  handleEdit: (
    data: Pick<
      OrganizationProfileInfo,
      | "contactPerson"
      | "contactNumber"
      | "timezoneId"
      | "officeNumber"
      | "mobileNumber"
    >,
  ) => void;
  handleDiscard: () => void;
};

export const EditOrganizationProfileBody = ({
  defaultValues,
  handleEdit,
  handleDiscard,
}: EditOrganizationProfileBodyProps) => {
  const dropdown: DropdownItem[] = timeZoneDropdown
    .filter((item) => item.country === (defaultValues?.country || "US"))
    ?.map((item) => ({ key: item.key, value: item.value }))
    ?.sort((a, b) => a.key.localeCompare(b.key));

  const [hasChanges, setHasChanges] = useState(false);
  const [isCreateOrganizationMode] = useAtom(isCreateOrganizationModeAtom);
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const isOrgNotYetCreated =
    isCreateOrganizationMode && (!selectedOrgId || selectedOrgId === 0);

  const { register, handleSubmit, errors } = useValidateForm(
    editOrganizationProfileBodySchema,
  );

  const handleChange = () => {
    setHasChanges(true);
  };

  return (
    <Box
      onSubmit={handleSubmit(handleEdit)}
      component={"form"}
      noValidate
      sx={{
        height: "100%",
      }}
    >
      <Grid
        container
        spacing={2}
        direction={{ lg: "row", xs: "column" }}
        sx={{
          width: "100%",
          height: "100%",
          px: 3,
          py: 3,
          alignItems: "flex-start",
        }}
      >
        <Grid
          container
          size={{ lg: 5, xs: 12 }}
          gap={3}
          sx={{
            px: { md: 4, xs: 0, sm: 2 },
            py: 0,
            flexDirection: "column",
          }}
        >
          <Grid container size={12}>
            <TextField
              register={register}
              defaultValue={defaultValues?.contactPerson}
              label="Contact Person"
              name="contactPerson"
              error={errors["contactPerson"]}
              onChange={handleChange}
              maxLength={100}
              disabled={isOrgNotYetCreated}
              required
            />
          </Grid>
          <Grid container size={12}>
            <TextField
              type="number"
              register={register}
              defaultValue={defaultValues?.contactNumber}
              label="Contact Number"
              name="contactNumber"
              error={errors["contactNumber"]}
              onChange={handleChange}
              allowScientificNotation={false}
              maxLength={20}
              disabled={isOrgNotYetCreated}
              required
            />
          </Grid>
          <Grid container size={12}>
            <TextField
              type="number"
              register={register}
              defaultValue={defaultValues?.officeNumber}
              label="Office Number"
              name="officeNumber"
              error={errors["officeNumber"]}
              onChange={handleChange}
              allowScientificNotation={false}
              maxLength={20}
              disabled={isOrgNotYetCreated}
              required
            />
          </Grid>
        </Grid>
        <Grid
          container
          size={{ lg: 5, xs: 12 }}
          gap={3}
          sx={{
            px: { md: 4, xs: 0, sm: 2 },
            py: 1.6,
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          <Grid container size={12}>
            <TextField
              type="number"
              register={register}
              defaultValue={defaultValues?.mobileNumber}
              label="Mobile Number"
              name="mobileNumber"
              error={errors["mobileNumber"]}
              onChange={handleChange}
              allowScientificNotation={false}
              maxLength={20}
              disabled={isOrgNotYetCreated}
              required
            />
          </Grid>
          <Grid container size={12}>
            <SelectBox
              register={register}
              defaultValue={defaultValues?.timezoneId}
              label="Timezone"
              name="timezoneId"
              dropdown={dropdown}
              error={errors["timezoneId"]}
              onChange={handleChange}
              valueAsNumber={true}
              disabled={isOrgNotYetCreated}
              required
            />
          </Grid>
        </Grid>
        <Grid
          container
          size={{ xs: 12, lg: 2 }}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-end",
            pl: { md: 4, xs: 0, sm: 2, lg: 0 },
            pr: { md: 4, xs: 0, sm: 2, lg: 2 },
            pt: { lg: 0, xs: 2 },
            pb: 0,
            height: { lg: "100%", xs: "fit-content" },
          }}
        >
          {!isCreateOrganizationMode && (
            <Button
              label="Discard"
              variantType={"secondary"}
              sx={{
                padding: "0.5rem",
                width: "100%",
              }}
              onClick={handleDiscard}
            />
          )}
          <Button
            type={hasChanges ? "submit" : undefined}
            disabled={!hasChanges || isOrgNotYetCreated}
            label="Save Changes"
            variantType={
              hasChanges && !isOrgNotYetCreated ? "primary" : "secondary"
            }
            sx={{
              padding: "0.5rem",
              width: "100%",
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
